import { PopoverSelector } from '@components/shared/popover-selector.component';
import { useStore } from '@tanstack/react-form';
import { useFieldContext, useFormContext } from './form';
import { FormErrorMessage, FormField, FormLabel } from './shared.component';

type SelectPopoverFieldProps = Omit<React.ComponentProps<typeof PopoverSelector>, 'value' | 'setValue' | 'hasError'> & {
  allowClear?: boolean;
  className?: string;
  label?: string;
  required?: boolean;
  tooltip?: React.ReactNode;
};

/**
 * A wrapper component over the PopoverSelector that adds TanStack Form logic and validation.
 * Provides form field context, error handling, accessibility features, and searchable dropdown functionality.
 * @param props - PopoverSelector props plus optional styling and form configuration
 * @returns A form-integrated popover select field component with search capabilities
 */
export function SelectPopoverField(props: SelectPopoverFieldProps) {
  const { allowClear = true, className, label, required, tooltip, ...otherProps } = props;
  const field = useFieldContext<string | undefined | null>();
  const form = useFormContext();

  const isSubmitting = useStore(form.store, (state) => state.isSubmitting);

  return (
    <FormField className={className}>
      {label && (
        <FormLabel
          htmlFor={field.name}
          required={required}
          tooltip={tooltip}
        >
          {label}
        </FormLabel>
      )}
      <PopoverSelector
        {...otherProps}
        disabled={isSubmitting || otherProps.disabled}
        hasError={!field.state.meta.isValid}
        name={field.name}
        onBlur={field.handleBlur}
        onClear={allowClear ? () => field.handleChange(null) : undefined}
        setValue={(value) => field.handleChange(value)}
        value={field.state.value ?? ''}
      />
      <FormErrorMessage />
    </FormField>
  );
}
